import { z } from "zod";
// What every field has. `label` is the administrator's wording, called "Name"
// on the properties panel; `label` here because a form's `name` usually means
// the key a value is stored under, and that is `id` rather than anything an
// administrator types. Values key on `id`, so a relabelling never orphans one.
export const FIELD_NAME_MESSAGE = "Every field needs a name";
export const OPTION_LABELS_MESSAGE = "Every option needs a name";
export const FieldBaseSchema = z.object({
    id: z.guid(),
    label: z.string().trim().min(1, FIELD_NAME_MESSAGE),
    // How to answer the field, shown to whoever fills it in. Optional: a field
    // without instructions shows none.
    instructions: z.string().trim().optional(),
    required: z.boolean(),
});
/** A control that shows a placeholder until it has a value: text, a number, a list to choose from. */
export const placeholderFieldSchema = (control) => FieldBaseSchema.extend({
    control: z.literal(control),
    placeholder: z.string(),
});
/**
 * One of the choices a control offers: what a practitioner sees, and the
 * value it stands for. The value is the option's identity — what is recorded
 * and what anything reading the data gets — so within a field each is unique
 * and none is empty: an empty value would read as "unanswered", and two the
 * same would be indistinguishable.
 */
export const OPTION_VALUES_MESSAGE = "Each option needs a value of its own";
/**
 * An option's value, on its own. A value is a code that other systems and
 * reports receive, so it is one line of visible characters — the only
 * whitespace a plain space — and short. No case or character set is imposed
 * beyond that: the reason to set a value is to match a code that already
 * exists elsewhere, like "GP" or "DV-01".
 */
export const OptionValueSchema = z
    .string()
    .trim()
    .max(100, "Keep option values to 100 characters")
    .regex(/^(?:[^\p{C}\p{Z}]| )*$/u, "Option values can't contain line breaks, tabs or hidden characters");
/**
 * What decides whether two options are the same choice: values that differ
 * only in case would read as one in any report, so they are one.
 */
export const optionValueKey = (value) => value.trim().toLowerCase();
export const OptionSchema = z.object({
    label: z.string().trim().min(1, OPTION_LABELS_MESSAGE),
    value: OptionValueSchema,
});
/**
 * The choices a control offers: at least one, or there is nothing to choose.
 * A value that is empty or shared with another option is reported at that
 * option, so whatever shows the problem can say which one.
 */
export const FieldOptionsSchema = OptionSchema.array()
    .min(1)
    .superRefine((options, ctx) => {
    const keys = options.map((option) => optionValueKey(option.value));
    keys.forEach((key, index) => {
        if (key === "" || keys.indexOf(key) !== keys.lastIndexOf(key)) {
            ctx.addIssue({
                code: "custom",
                message: OPTION_VALUES_MESSAGE,
                path: [index, "value"],
            });
        }
    });
});
export const ChoiceOptionsSchema = z.object({ options: FieldOptionsSchema });
/**
 * One control, described once: what the library calls it, what a field of it
 * holds, what an answer to one may be, and what a new field of it starts as. How it is drawn lives in the
 * app, keyed by the same `key`, so this package never imports React.
 *
 * `key` is checked against the schema's own `control` literal, and `empty` is
 * what a new field holds besides its id, control and name. A new field is
 * named for its control, so every field has a name from the start and the
 * builder shows what was added rather than a blank. A new field is parsed by
 * the schema, so a definition whose defaults do not satisfy it fails at the
 * first field rather than at the first save.
 */
export function defineControl({ key, label, schema, answer, isClearable, empty, }) {
    return {
        key,
        label,
        schema,
        answer,
        isClearable,
        create: (id) => schema.parse({ id, control: key, label, ...empty }),
    };
}
